// stripeService.js
import { loadStripe } from '@stripe/stripe-js';

let stripePromise;

const getStripe = async () => {
  if (!stripePromise) {
    try {
      const res = await fetch('/api/config/stripe-config');
      if (!res.ok) throw new Error('Failed to fetch Stripe config');
      const { publicKey } = await res.json();
      if (!publicKey) throw new Error('Stripe publishable key not found in server response');
      stripePromise = loadStripe(publicKey);
    } catch (error) {

      return null; 
    }
  }
  return stripePromise;
};

// Fetch price IDs from server configuration
let priceIdsCache = null;

export const getPriceIds = async () => {
  if (!priceIdsCache) {
    try {
      const response = await fetch('/api/config/stripe-prices');
      if (!response.ok) throw new Error('Failed to fetch price IDs');
      priceIdsCache = await response.json();
    } catch (error) {

      // If fetch fails, throw error - no fallback values
      throw new Error('Failed to fetch price IDs from server');
    }
  }
  return priceIdsCache;
};

// Legacy export for backwards compatibility
export const PRICE_IDS = {
  get ESSENTIAL() { throw new Error('Use getPriceIds() instead'); },
  get PRO() { throw new Error('Use getPriceIds() instead'); },
  get PRO_TRIAL() { throw new Error('Use getPriceIds() instead'); }
};

/**
 * Create a Stripe checkout session for subscription
 */
export async function createCheckoutSession(priceId, mode = 'subscription') {
  try {
    const response = await fetch('/api/stripe/create-checkout-session', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        priceId,
        mode,
        successUrl: `${window.location.origin}/signup/paid?session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${window.location.origin}/pricing`
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to create checkout session');
    }

    const { sessionId } = await response.json();
    
    const stripe = await getStripe();
    if (!stripe) {
      throw new Error('Failed to initialize Stripe');
    }
    const { error } = await stripe.redirectToCheckout({ sessionId });
    
    if (error) {
      throw error;
    }
  } catch (error) {

    throw error;
  }
}

/**
 * Verify a Stripe checkout session
 */
export async function verifyCheckoutSession(sessionId) {
  try {
    const response = await fetch(`/api/stripe/verify-session/${sessionId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to verify session');
    }

    return await response.json();
  } catch (error) {

    throw error;
  }
}

/**
 * Mark a Stripe session as used after successful account creation
 */
export async function markSessionAsUsed(sessionId, userId, email) {
  try {
    const response = await fetch(`/api/stripe/mark-session-used/${sessionId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        email
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to mark session as used');
    }

    return await response.json();
  } catch (error) {

    throw error;
  }
}

/**
 * Start a 21-day trial (no payment required)
 */
export async function startTrial() {
  return {
    type: 'trial',
    plan: 'pro',
    trialDays: 21,
    status: 'trialing'
  };
}

// Export getStripe for external use
export { getStripe };